// Updated HistoricalAtonChart component - no longer fetches services separately
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ChartContainer, LoadingSpinner } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useFetchServiceGraph } from '@ais-ui/api';
import { AtonHostDetails } from '../../types';
import { useLocalization } from '@ais-ui/locale-management';

// Data point interface matching the ship chart format
export interface AtonDataPoint {
  index: number;
  timestamp: string; // ISO string
  value: number;
  valueLabel?: string;
}

// Track data structure
export interface AtonTrackData {
  track: {
    coordinates: AtonDataPoint[];
  };
}

// Props interface
export interface AtonChartProps {
  chartData: AtonTrackData | null;
  className?: string;
  dataType: string;
  yAxisLabel: string;
  color?: string;
  thresholds?: {
    alarmThreshold?: number;
    warningThreshold?: number;
    alarmRange?: { min: number; max: number };
    warningRange?: { min: number; max: number };
  };
}

interface CustomTooltipContentProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

export default function AtonChart({
  chartData,
  className,
  dataType,
  yAxisLabel,
  color = '#00647a',
  thresholds,
}: AtonChartProps): React.JSX.Element {
  const { messages } = useLocalization();
  const dataLabel = useMemo(() => {
    return yAxisLabel;
  }, [yAxisLabel]);

  const noDataMessage = messages.emptyStateNoData?.replace('{entity}', 'AtoN') ?? 'No AtoN data available';

  // Transform track data for chart display
  const transformedChartData = useMemo((): AtonDataPoint[] => {
    if (!chartData?.track?.coordinates?.length) {
      return [];
    }

    return chartData.track.coordinates.map((point, index) => ({
      index,
      timestamp: point.timestamp,
      value: point.value,
      valueLabel: dataType,
    }));
  }, [chartData, dataType]);

  // Format x-axis ticks
  const formatXAxis = (timestamp: string): string => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM dd HH:mm');
    } catch (error) {
      console.error('Error formatting date:', error);
      return timestamp;
    }
  };

  // Format tooltip timestamp
  const formatTooltipDate = (timestamp: string): string => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch (error) {
      console.error('Error formatting tooltip date:', error);
      return timestamp;
    }
  };

  // Calculate y-axis domain based on data and thresholds
  const yAxisDomain = useMemo((): [number, number] => {
    if (!transformedChartData.length) {
      return [0, 100]; // Default range
    }

    const values = transformedChartData.map((item) => item.value);
    let minVal = Math.min(...values);
    let maxVal = Math.max(...values);

    // Include thresholds in domain calculation
    if (thresholds) {
      if (thresholds.alarmThreshold !== undefined) {
        minVal = Math.min(minVal, thresholds.alarmThreshold);
        maxVal = Math.max(maxVal, thresholds.alarmThreshold);
      }
      if (thresholds.warningThreshold !== undefined) {
        minVal = Math.min(minVal, thresholds.warningThreshold);
        maxVal = Math.max(maxVal, thresholds.warningThreshold);
      }
      if (thresholds.alarmRange) {
        minVal = Math.min(minVal, thresholds.alarmRange.min);
        maxVal = Math.max(maxVal, thresholds.alarmRange.max);
      }
      if (thresholds.warningRange) {
        minVal = Math.min(minVal, thresholds.warningRange.min);
        maxVal = Math.max(maxVal, thresholds.warningRange.max);
      }
    }

    // Add 10% padding
    const range = maxVal - minVal;
    const padding = range * 0.1;

    return [Math.floor(minVal - padding), Math.ceil(maxVal + padding)];
  }, [transformedChartData, thresholds]);

  // Custom tooltip content component
  const CustomTooltipContent = ({
    active,
    payload,
    label,
  }: CustomTooltipContentProps): React.JSX.Element | null => {
    if (active && payload?.length && label) {
      const value = payload[0].value;

      return (
        <div className="bg-card p-3 border border-border rounded-lg shadow-md">
          <p className="font-semibold text-foreground">
            {formatTooltipDate(label)}
          </p>
          <p className="text-muted-foreground">
            <span className="font-medium">{dataLabel}:</span>{' '}
            <span className="font-mono">{value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Show empty state if no data - using consistent height
  if (!transformedChartData.length) {
    return (
      <div className={cn('w-full space-y-4 rounded-lg p-6', className)}>
        <div className="h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="text-muted-foreground text-sm">{noDataMessage}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full space-y-4 rounded-lg p-6', className)}>
      <ChartContainer
        config={{
          value: {
            label: dataLabel,
            color: `var(--color-primary, ${color})`,
          },
        }}
        className="h-[400px] w-full"
      >
        <LineChart
          data={transformedChartData}
          margin={{ top: 20, right: 30, bottom: 80, left: 40 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f0f0f0"
          />
          <XAxis
            dataKey="timestamp"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatXAxis}
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 12, fill: '#666' }}
            label={{
              value: 'Date & Time',
              position: 'insideBottom',
              offset: -60,
              style: { textAnchor: 'middle', fontSize: '12px', fill: '#666' },
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={yAxisDomain}
            tick={{ fontSize: 12, fill: '#666' }}
            label={{
              value: dataLabel,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: '#666' },
            }}
          />

          {/* Reference lines for thresholds and ranges */}
          {thresholds?.alarmThreshold !== undefined && (
            <ReferenceLine
              y={thresholds.alarmThreshold}
              stroke="#ef4444"
              strokeDasharray="5 5"
              strokeWidth={2}
            />
          )}

          {thresholds?.warningThreshold !== undefined && (
            <ReferenceLine
              y={thresholds.warningThreshold}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              strokeWidth={2}
            />
          )}

          {thresholds?.alarmRange && (
            <>
              <ReferenceLine
                y={thresholds.alarmRange.min}
                stroke="#ef4444"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={thresholds.alarmRange.max}
                stroke="#ef4444"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
            </>
          )}

          {thresholds?.warningRange && (
            <>
              <ReferenceLine
                y={thresholds.warningRange.min}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={thresholds.warningRange.max}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                strokeWidth={1}
              />
            </>
          )}

          <Tooltip
            content={<CustomTooltipContent />}
            isAnimationActive={false}
            cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '5 5' }}
          />
          <Line
            type="monotone"
            dataKey="value"
            name={dataLabel}
            stroke={color}
            strokeWidth={2}
            dot={{
              fill: color,
              strokeWidth: 0,
              r: 3,
            }}
            activeDot={{
              r: 6,
              fill: color,
              stroke: 'white',
              strokeWidth: 2,
            }}
            isAnimationActive={true}
            animationDuration={1000}
          />
        </LineChart>
      </ChartContainer>
    </div>
  );
}

// Parser function remains the same
export function parseAtonGraphData(htmlResponse: string): {
  title: string;
  hostName: string;
  serviceName: string;
  data: AtonDataPoint[];
  timeRange: {
    start: Date;
    end: Date;
    step: number;
  };
} | null {
  try {
    const createGraphIndex = htmlResponse.indexOf('cmk.graphs.create_graph(');
    if (createGraphIndex === -1) {
      console.error('Could not find create_graph function');
      return null;
    }

    const afterFunction = htmlResponse.substring(
      createGraphIndex + 'cmk.graphs.create_graph('.length
    );

    const firstCommaIndex = afterFunction.indexOf(',');
    if (firstCommaIndex === -1) {
      console.error('Could not find first parameter comma');
      return null;
    }

    const afterFirstParam = afterFunction.substring(firstCommaIndex + 1).trim();
    const startIndex = afterFirstParam.indexOf('{');
    if (startIndex === -1) {
      console.error('Could not find JSON start');
      return null;
    }

    let braceCount = 0;
    let endIndex = -1;

    for (let i = startIndex; i < afterFirstParam.length; i++) {
      if (afterFirstParam[i] === '{') {
        braceCount++;
      } else if (afterFirstParam[i] === '}') {
        braceCount--;
        if (braceCount === 0) {
          endIndex = i;
          break;
        }
      }
    }

    if (endIndex === -1) {
      console.error('Could not find JSON end');
      return null;
    }

    const jsonString = afterFirstParam.substring(startIndex, endIndex + 1);
    const graphData = JSON.parse(jsonString);

    const title = graphData.title || 'AtoN Graph';
    const startTime = graphData.start_time;
    const endTime = graphData.end_time;
    const step = graphData.step;

    const startDate = new Date(startTime * 1000);
    const endDate = new Date(endTime * 1000);

    const hostName =
      graphData.definition?.specification?.host_name || 'Unknown';
    const serviceName =
      graphData.definition?.specification?.service_description || 'Unknown';

    const dataPoints: AtonDataPoint[] = [];

    if (graphData.curves && graphData.curves.length > 0) {
      const curve = graphData.curves[0];
      if (curve.points && Array.isArray(curve.points)) {
        curve.points.forEach((point: [number, number], index: number) => {
          if (Array.isArray(point) && point.length >= 2 && point[1] !== null) {
            const timestamp = startTime + index * step;
            const value = point[1];

            if (typeof value === 'number' && !isNaN(value)) {
              const pointDate = new Date(timestamp * 1000);
              dataPoints.push({
                timestamp: pointDate.toISOString(),
                value,
                index,
              });
            }
          }
        });
      }
    }

    return {
      title,
      hostName,
      serviceName,
      data: dataPoints,
      timeRange: {
        start: startDate,
        end: endDate,
        step: step,
      },
    };
  } catch (error) {
    console.error('Error parsing AtoN graph data:', error);
    return null;
  }
}

interface HistoricalAtonChartProps {
  aton: AtonHostDetails;
  serviceName: string | null;
}

export const HistoricalAtonChart = ({
  aton,
  serviceName,
}: HistoricalAtonChartProps) => {
  const { messages } = useLocalization();
  const [chartData, setChartData] = useState<AtonTrackData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServiceGraph = useFetchServiceGraph();

  // Find the matching monitor to get threshold data
  const monitorThresholds = useMemo(() => {
    if (!aton.monitors || !serviceName) return undefined;

    // Try exact name match first
    let monitor = aton.monitors.find((m) => m.name === serviceName);

    // If no exact match, try AIS message type mapping
    if (!monitor) {
      const serviceLower = serviceName.toLowerCase();
      monitor = aton.monitors.find((m) => {
        const nameLower = m.name.toLowerCase();

        // Map message types to AIS types
        if (serviceLower === 'msg21' && nameLower.includes('ais type 21')) {
          return true;
        }
        if (serviceLower === 'msg6' && nameLower.includes('ais type 6')) {
          return true;
        }
        if (serviceLower === 'msg8' && nameLower.includes('ais type 8')) {
          return true;
        }
        if (
          serviceLower.includes('ais') &&
          serviceLower.includes('indicator') &&
          nameLower.includes('ais indicator')
        ) {
          return true;
        }

        return false;
      });
    }

    // If still no match, try partial matching for other services
    if (!monitor) {
      monitor = aton.monitors.find(
        (m) =>
          m.name.toLowerCase().includes(serviceName.toLowerCase()) ||
          serviceName.toLowerCase().includes(m.name.toLowerCase())
      );
    }

    if (!monitor) return undefined;

    return {
      alarmThreshold: monitor.alarmThreshold,
      warningThreshold: monitor.warningThreshold,
      alarmRange: monitor.alarmRange,
      warningRange: monitor.warningRange,
    };
  }, [aton.monitors, serviceName]);

  useEffect(() => {
    const loadData = async () => {
      if (!aton?.id) return;
      if (!serviceName) return;

      setLoading(true);
      setError(null);

      try {
        const resultGraph = await fetchServiceGraph(aton.id, serviceName);
        if (resultGraph) {
          const adaptedData: AtonTrackData = {
            track: {
              coordinates: resultGraph.data,
            },
          };
          setChartData(adaptedData);
        } else {
          setError(
            messages.atonHistoricalAtonChartLoadDataParseGraphErrorMessage
          );
        }
      } catch (err) {
        setError(messages.atonHistoricalAtonChartLoadDataFetchAtonErrorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [aton, fetchServiceGraph, serviceName]);

  if (loading) {
    return (
      <div className="w-full space-y-4 rounded-lg p-6 bg-card">
        <div className="h-[400px] flex items-center justify-center rounded-lg">
          <div className="text-center">
            <LoadingSpinner></LoadingSpinner>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full space-y-4 rounded-lg p-6 bg-card">
        <div className="h-[400px] flex items-center justify-center bg-red-50 rounded-lg border border-red-200">
          <div className="text-center">
            <div className="text-lg text-red-600 font-medium">Error</div>
            <div className="text-sm text-red-500 mt-1">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AtonChart
      chartData={chartData}
      yAxisLabel={serviceName || ''}
      dataType="msg6"
      color="#00647a"
      className="bg-card"
      thresholds={monitorThresholds}
    />
  );
};
